import { appStore, IEvent, MessageBus } from '@tom-ui/utils'
import { ContainerResponse, JourneyResponse, OccupancyType, PositionDto } from '@yard/app/api'
import { EventTypes } from '@yard/messages/eventsTypes'
import {
  mapContainerUpdateToOccupancy,
  mapJourneyUpdateToOccupancy,
  mapJourneyWithPositionToOccupancy,
  mapStackedInOccupancyToOccupancy,
} from '@yard/pages/YardSetup/helpers/occupancy.mapper'
import { StackedInResponse } from '@yard/pages/YardSetup/models/occupancy.model'
import { occupancyService } from '@yard/services/occupancy.service'
import { makeObservable, observable, runInAction } from 'mobx'
import { JourneyStore } from './journey.store'

export interface OccupancyDataDto {
  containerId: number
  journeyId: number
  position?: PositionDto | null
  type?: OccupancyType
}

export type OcuppancyWithFilterData = OccupancyDataDto &
  Pick<
    JourneyResponse,
    | 'inboundCarrierType'
    | 'inboundCarrierVisitId'
    | 'outboundCarrierType'
    | 'outboundCarrierVisitId'
    | 'inboundConsigneeId'
    | 'outboundConsigneeId'
    | 'inboundCustomerId'
    | 'outboundCustomerId'
    | 'inboundIsDangerous'
    | 'outboundIsDangerous'
    | 'inboundIsEmpty'
    | 'outboundIsEmpty'
    | 'inboundIsOOG'
    | 'outboundIsOOG'
    | 'portOfDischarge'
    | 'portOfLoading'
  > &
  Pick<
    ContainerResponse,
    | 'containerHeight'
    | 'containerNumber'
    | 'containerType'
    | 'isDamaged'
    | 'isReefer'
    | 'isoCode'
    | 'length'
    | 'operatorId'
  >

export class OccupancyStore {
  occupanciesByBlock: Record<string, OcuppancyWithFilterData[]> = {}

  private readonly disposers: Array<() => void> = []

  constructor(
    private readonly messageBus: MessageBus,
    private readonly journeyStore: JourneyStore,
  ) {
    makeObservable(this, {
      occupanciesByBlock: observable,
    })

    this.disposers.push(
      this.messageBus.subscribeEvent<StackedInResponse[]>(
        EventTypes.StackedInEvent,
        this.handleOccupancyUpsertedEvent,
      ),
      this.messageBus.subscribeEvent<JourneyResponse>(
        EventTypes.JourneyUpsertedEvent,
        this.handleJourneyUpsertedEvent,
      ),
      this.messageBus.subscribeEvent<ContainerResponse[]>(
        EventTypes.ContainerUpsertedEvent,
        this.handleContainerUpsertedEvent,
      ),
    )
  }

  dispose(): void {
    this.disposers.forEach(d => d())
    this.disposers.length = 0
  }

  async loadOccupancies() {
    try {
      const data = await occupancyService.getOccupancies()

      runInAction(() => {
        this.occupanciesByBlock = {}

        data.forEach(block => {
          const blockOccupancies: OcuppancyWithFilterData[] = []

          block.journeyWithPositions.forEach(jwp => {
            if (jwp.positionsWithType && jwp.positionsWithType.length > 0) {
              jwp.positionsWithType.forEach(p => {
                blockOccupancies.push(
                  mapJourneyWithPositionToOccupancy(
                    jwp,
                    { bay: p.bay, row: p.row, tier: p.tier },
                    p.type,
                  ),
                )
              })
            } else if (jwp.positions && jwp.positions.length > 0) {
              jwp.positions.forEach(position => {
                blockOccupancies.push(
                  mapJourneyWithPositionToOccupancy(jwp, position, OccupancyType.Occupied),
                )
              })
            } else {
              blockOccupancies.push(mapJourneyWithPositionToOccupancy(jwp))
            }
          })

          this.occupanciesByBlock[block.blockId] = blockOccupancies
        })
      })
    } catch (e) {
      appStore.setShowAlert('error', (e as any)?.response?.data?.detail ?? 'An error occurred')
    }
  }

  private readonly handleOccupancyUpsertedEvent = (event: IEvent<StackedInResponse[]>) => {
    const upsertedOccupancies = event.payload

    if (!upsertedOccupancies) return

    runInAction(() => {
      Object.keys(this.occupanciesByBlock).forEach(existingBlockId => {
        const occupancies = this.occupanciesByBlock[existingBlockId] || []
        const filteredOccupancies = occupancies.filter(
          o => !upsertedOccupancies.some(uo => o.journeyId === uo.journeyId),
        )

        if (filteredOccupancies.length !== occupancies.length)
          this.occupanciesByBlock[existingBlockId] = filteredOccupancies
      })

      upsertedOccupancies.forEach(upsertedOccupancy => {
        const occupancies = this.occupanciesByBlock[upsertedOccupancy.blockId] || []

        const hasJourney = upsertedOccupancy.journeyId in this.journeyStore.journeys

        if (!hasJourney) return

        const journey = this.journeyStore.journeys[upsertedOccupancy.journeyId]

        this.occupanciesByBlock[upsertedOccupancy.blockId] = [
          ...occupancies,
          mapStackedInOccupancyToOccupancy(upsertedOccupancy, journey),
        ]
      })
    })
  }

  private readonly handleJourneyUpsertedEvent = (event: IEvent<JourneyResponse>) => {
    const upsertedJourney = event.payload

    if (!upsertedJourney) return

    runInAction(() => {
      Object.keys(this.occupanciesByBlock).forEach(blockId => {
        this.occupanciesByBlock[blockId] = this.occupanciesByBlock[blockId].map(occupancy => {
          if (upsertedJourney.id !== occupancy.journeyId) return occupancy

          return mapJourneyUpdateToOccupancy(occupancy, upsertedJourney)
        })
      })
    })
  }

  private readonly handleContainerUpsertedEvent = (event: IEvent<ContainerResponse[]>) => {
    const upsertedContainers = event.payload

    if (!upsertedContainers) return

    runInAction(() => {
      Object.keys(this.occupanciesByBlock).forEach(blockId => {
        this.occupanciesByBlock[blockId] = this.occupanciesByBlock[blockId].map(occupancy => {
          const updatedContainer = upsertedContainers.find(
            c => c.containerNumber === occupancy.containerNumber,
          )
          if (!updatedContainer) return occupancy

          return mapContainerUpdateToOccupancy(occupancy, updatedContainer)
        })
      })
    })
  }
}
